<template>
  <main class="page">
    <Transition
      name="fade">
      <div
        v-show="loading"
        class="loading"></div>
    </Transition>

    <div
      v-if="data"
      class="content">
      <div v-if="featuredImage || data.intro || data.intro_quote || data.intro_video">
        <Intros
          :featured-image="featuredImage"
          :intro="data.intro"
          :intro-quote="data.intro_quote"
          :intro-video="data.intro_video" />
      </div>
      <div
        v-if="data.content"
        class="content__rows first">
        <Section
          v-for="(section, key) in data.content"
          :key="`section${key}`"
          :section="section" />
      </div>

      <!-- Awards -->
      <div
        v-if="data.precinct || data.nishi || data.pavilion"
        class="awards">
        <h2 class="ucase grey">
          Awards
        </h2>
        <Table
          v-if="data.precinct"
          :heading="'NewActon Precinct'"
          :content="data.precinct" />
        <Table
          v-if="data.nishi"
          :heading="'NewActon Nishi'"
          :content="data.nishi" />
        <Table
          v-if="data.pavilion"
          :heading="'NewActon Pavilion'"
          :content="data.pavilion" />
      </div>

      <!-- Highlights -->
      <div
        v-if="data.highlights"
        class="highlights">
        <h2
          v-if="data.highlights.title"
          class="ucase grey">
          {{ data.highlights.title }}
        </h2>
        <ul>
          <li
            v-for="(highlight, key) in data.highlights.items"
            :key="`highlight${key}`"
            class="highlight">
            <a
              target="_blank"
              rel="noopener noreferrer"
              :href="getHighlightLink(highlight)">
              <div class="highlight__image">
                <img :src="highlight.image.url">
              </div>
              <p v-html="highlight.name"></p>
            </a>
          </li>
        </ul>
      </div>


      <!-- Galleries -->
      <section
        v-if="data.images"
        class="galleries">
        <ul
          class="galleries__list">
          <li
            v-for="(item, key) in data.images"
            :key="`image${key}`"
            @click="activeGalleries = key">
            <img :src="item.image.sizes.medium">
          </li>
        </ul>
        <aside
          class="galleries__lightbox"
          :class="{'galleries__lightbox--active': (activeGalleries || activeGalleries === 0) }"
          @click="activeGalleries = null">
          <div
            class="galleries__lightbox-container"
            :class="{'galleries__lightbox-container--active': (activeGalleries || activeGalleries === 0)}">
            <img
              v-if="data.images[activeGalleries]"
              :src="data.images[activeGalleries].image.url"
              :class="{
                'active': activeGalleries,
                'landscape': data.images[activeGalleries].image.width > data.images[activeGalleries].image.height,
                'portrait': data.images[activeGalleries].image.height >= data.images[activeGalleries].image.width
              }">
            <p v-if="data.images[activeGalleries]">
              {{ data.images[activeGalleries].image.caption }}
            </p>
          </div>
        </aside>
      </section>

      <!-- Media -->
      <div
        v-if="data.media || data.list"
        class="media">
        <h2 class="ucase grey">
          Selected Media
        </h2>
        <Table
          v-if="data.media || data.list"
          :content="data.media || data.list" />
      </div>

      <!-- Disclaimer -->
      <div
        v-if="data.disclaimer"
        class="disclaimer">
        <h2 class="grey">
          Disclaimer
        </h2>
        <p class="bold">
          {{ data.disclaimer }}
        </p>
      </div>

      <!-- JLL Contacts -->
      <Contacts
        v-if="data.jll_contacts || data.jll_logo"
        :heading="`Please contact the exclusive agents`"
        :content="data.jll_contacts"
        :logo="data.jll_logo"
        :footer="data.contacts_footer"
        :sec-logo="data.secondary_logo" />
    </div>
    <!-- <page-navigation /> -->
  </main>
</template>

<script>
import Intros from '@/components/Intros'
import Section from '@/components/sections/Section'
import Table from '@/components/Table'
import Contacts from '@/components/Contacts'
import PageNavigation from '@/components/PageNavigation'

export default {
  name: 'Page',
  components: {
    Section,
    Intros,
    Table,
    Contacts,
  },
  data() {
    return {
      loading: false,
      data: null,
      activeGalleries: null
    }
  },
  computed: {
    featuredImage() {
      return (this.data.featured_image === false ? null : this.data.featured_image);
    }
  },
  watch: {
    $route(to, from) {
      this.fetchData()
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData: function() {
      // set content to null and add loading state
      this.data = null
      this.loading = true

      // api request
      this.$http.get(this.$route.meta.apiPath).then(response => {

        // get body data and remove loading state
        this.data = response.body;
        this.loading = false

      }, response => {
        // error callback and remove loading state
        this.loading = false
      });
    },
    getHighlightLink(highlight) {
      if (highlight.url) return highlight.url

      if (highlight.file) return highlight.file.url

      return '#'
    }
  }
}
</script>

<style lang="scss" scoped>
.page {
  width:100%;

  .loading {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: $color--base;
    z-index: 2;
  }

  .media {
    padding: $padding--base 0;

    @include mobile {
      padding: $padding--base-mbl 0;
    }
  }

  .disclaimer {
    @include section;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s ease-in-out;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
    transition-delay: .25s;
  }

  .galleries {
    &__list {
      display:flex;
      flex-wrap:wrap;
      li {
        width:calc(20% - (#{$gutter--base * 4} / 5));
        margin: 0 $gutter--base $gutter--base 0;
        cursor:pointer;
        &:nth-child(5n + 5) {
          margin-right:0px;
        }

        img {
          float:left;
          width:100%;
          height: calc((20vw - (#{$gutter--base * 2})) * 0.667);
          object-fit: cover;
        }
      }
    }

    &__lightbox {
      position: fixed;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      cursor: zoom-out;
      background-color: transparent;
      transition: background-color 200ms ease-in-out;

      &--active {
        width: 100%;
        height: 100%;
        background-color: rgba(0,0,0,0.66);
      }
      &-container {
        opacity: 0;
        transition: opacity 400ms ease-in-out;
        transition-delay: 200ms;
        &--active {
          opacity:1;
        }
      }

      img {
        max-width:90vw;
        max-height:90vw;
      }

      p {
        position: absolute;
        bottom: $gutter--base;
        font-weight: bold;
        left: 50%;
        width: 90%;
        transform: translateX(-50%);
        text-align: center;
      }
    }

    @include mobile {
      &__list {
        li {
          width:calc(33.3% - (#{$gutter--base-mbl * 2} / 3));
          margin: 0 $gutter--base-mbl $gutter--base-mbl 0 !important;

          &:nth-child(3n + 3) {
            margin-right:0px !important;
          }

          img {
            height: calc((33.3vw - (#{$gutter--base-mbl * 2})) * 0.667);
          }
        }
      }
    }
  }

  .highlights {
    ul {
      display:flex;
      flex-wrap:wrap;
      width:80%;
      max-width:1024px;
      margin:$padding--base auto 0 auto;
    }
    .highlight {
      width:calc(33.3% - (#{$gutter--base * 2} / 3));
      margin:0 $gutter--base $padding--base 0;

      &:nth-child(3n + 3) {
        margin-right:0px;
      }

      &__image {
        img {
          filter:grayscale(1);
          transition:filter 200ms ease-in-out;
        }

        &:hover {
          img {
            filter:grayscale(0);
          }
        }
      }

      p {
        font-weight: bold;
        margin:0.625rem 0 0 0;
        color:#fff;
      }
    }

    @include mobile {
      ul {
        width:100%;
        margin:$padding--base-mbl auto 0 auto;
      }

      .highlight {
        width:calc(50% - (#{$gutter--base * 1} / 2));
        margin:0 $gutter--base-mbl $padding--base-mbl 0 !important;
        &:nth-child(2n + 2) {
          margin-right:0px !important;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.content__rows.first {
  > section:first-child .content__row--image {
    padding-top: rem(20)
  }
}
</style>
